import { useCallback, useEffect, useRef, useState } from "react";
import { erTom, tomtDok, type Tekstdok } from "@infoflyt/shared/tekst";
import { useGlidendeKort } from "./glidendeKort";
import { Rikfelt } from "./Rikfelt";
import * as tekst from "../state/tekstflater";
import { useLevende } from "../state/levende";

/**
 * Et bord med tekstflater: ett kort per felt.
 *
 * FELTENE ER PÅ BREDDE MED EN TELEFON. Det er ikke en tilfeldig måling: det
 * som skrives her leses der, og en linje som ser kort ut på en bred skjerm
 * blir tre linjer i lomma. Skriver man i samme spaltebredde som leseren får,
 * ser man hva man faktisk sender.
 *
 * LAGRES AV SEG SELV. Ingen lagre-knapp å glemme: halvannet sekund etter
 * siste tastetrykk, og uansett når feltet forlates. Linja i kortets fot sier
 * hva som er lagret — en flate som lagrer i det stille må kunne bevise det.
 *
 * Beskjeder og shotlist deler denne. De er samme maskin med ulikt innhold,
 * og en kopi til ville betydd at neste retting måtte gjøres to steder.
 */

/* Lenge nok til at en setning blir ferdig, kort nok til at man ikke rekker
   å lure på om noe ble borte. */
const PAUSE_MS = 1500;

type Status = "rolig" | "skriver" | "lagrer" | "lagret" | "feil";

interface Tilstand {
  readonly dok: Tekstdok;
  readonly status: Status;
  readonly endret: string | null;
}

export function Tekstbord({
  produksjonId, felter, plassholder, visTittel = true, listemodus = false,
  skrivbar,
}: {
  readonly produksjonId: string;
  readonly felter: readonly tekst.Felt[];
  plassholder(f: tekst.Felt): string;
  /**
   * Om DETTE feltet kan skrives i. Alt er skrivbart naar den ikke er satt.
   *
   * Briefen er det eneste stedet dette skiller: tre felt, tre
   * spesialtillatelser. Se BriefScreen og `tilgang.skriver_tekstflate`.
   */
  skrivbar?: (f: tekst.Felt) => boolean;
  /** Av når kortet er alene og fanen alt har sagt hva det er. */
  readonly visTittel?: boolean;
  /** Av «Huskeliste»-overskriften: i en liste der ALT er punkter, er den støy. */
  readonly listemodus?: boolean;
}): JSX.Element {
  const [laster, setLaster] = useState(true);
  /*
   * TILSTANDEN ER EN CACHE, IKKE EN FASTLAAST LISTE.
   *
   * Den ble foer saadd fra `felter` ÉN gang, ved oppstart. Kom det et felt
   * til etterpaa — fordi man slo paa et omraade til mens man sto i flata —
   * hadde det ingen plass aa lastes inn i, og ruta ble staaende tom til man
   * gikk ut og inn igjen. Naa lagres alt som leses, og det som VISES
   * bestemmes av `felter` ved tegning.
   */
  const [felt, setFelt] = useState<Readonly<Record<string, Tilstand>>>({});
  /* Nye kort glir inn, og naboene glir med. Se `glidendeKort.ts`. */
  const settKort = useGlidendeKort();

  const timere = useRef(new Map<string, number>());
  /* Det som faktisk ligger lagret, per felt. Uten dette lagrer vi tomme runder. */
  const lagret = useRef(new Map<string, string>());

  /* Feltet noen STÅR I. Se `last`. */
  const fokusert = useRef<string | null>(null);

  const last = useCallback((): void => {
    void (async () => {
      const rader = await tekst.hentAlle(produksjonId);
      setFelt((prev) => {
        const neste = { ...prev };
        for (const r of rader) {
          const her = neste[r.slug]
            ?? { dok: tomtDok(), status: "rolig" as Status, endret: null };
          /*
           * ET FELT NOEN SKRIVER I RØRES IKKE.
           *
           * Feltet er ukontrollert — nodene settes bare når dokumentet
           * kommer utenfra. Kom en oppdatering midt i en setning, ville
           * nodene blitt skrevet om under markøren, og den som skrev hadde
           * mistet både plassen sin og det siste hen tastet.
           */
          if (fokusert.current === r.slug) continue;
          if (her.status === "skriver" || her.status === "lagrer") continue;
          const naa = JSON.stringify(r.dok);
          if (naa === JSON.stringify(her.dok)) continue;
          lagret.current.set(r.slug, naa);
          neste[r.slug] = { dok: r.dok, status: "rolig", endret: r.endretIso };
        }
        return neste;
      });
      setLaster(false);
    })();
  }, [produksjonId]);

  useEffect(last, [last]);

  /* Skriver koordinatoren en beskjed mens noen leser den, kommer den fram. */
  useLevende(`tekst:${felter.map((f) => f.slug).join("+")}`, ["text_page"], last);

  // Et halvskrevet avsnitt skal ikke ligge igjen i en timer som aldri går.
  useEffect(() => {
    const t = timere.current;
    return () => { for (const id of t.values()) window.clearTimeout(id); };
  }, []);

  const skriv = useCallback(async (slug: string, dok: Tekstdok): Promise<void> => {
    const f = felter.find((x) => x.slug === slug);
    if (f === undefined) return;
    const naa = JSON.stringify(dok);
    if (naa === lagret.current.get(slug)) return;

    setFelt((p) => ({ ...p, [slug]: { ...p[slug]!, status: "lagrer" } }));
    const naar = await tekst.lagre(produksjonId, slug, f.tittel, dok);
    if (naar === null) {
      setFelt((p) => ({ ...p, [slug]: { ...p[slug]!, status: "feil" } }));
      return;
    }
    lagret.current.set(slug, naa);
    setFelt((p) => ({ ...p, [slug]: { ...p[slug]!, status: "lagret", endret: naar } }));
  }, [produksjonId, felter]);

  const endret = useCallback((slug: string, dok: Tekstdok): void => {
    setFelt((p) => ({ ...p, [slug]: { ...p[slug]!, dok, status: "skriver" } }));
    const gammel = timere.current.get(slug);
    if (gammel !== undefined) window.clearTimeout(gammel);
    timere.current.set(slug, window.setTimeout(() => { void skriv(slug, dok); }, PAUSE_MS));
  }, [skriv]);

  const ferdig = useCallback((slug: string, dok: Tekstdok): void => {
    const gammel = timere.current.get(slug);
    if (gammel !== undefined) window.clearTimeout(gammel);
    // Dokumentet kommer SORTERT tilbake fra feltet, saa tilstanden maa
    // oppdateres — ellers staar huskepunktene der de ble skrevet til neste
    // innlasting.
    setFelt((p) => ({ ...p, [slug]: { ...p[slug]!, dok } }));
    void skriv(slug, dok);
  }, [skriv]);

  return (
    <div className="beskjeder">
      <div className="beskjeder__bord">
        {felter.map((f) => {
          /* Ingen rad lest ennaa: tomt felt, ikke en manglende rute. */
          const t = felt[f.slug]
            ?? { dok: tomtDok(), status: "rolig" as Status, endret: null };
          return (
            <section
              key={f.slug}
              ref={(el) => settKort(f.slug, el)}
              className="beskjed"
              aria-label={f.tittel}
              onFocusCapture={() => { fokusert.current = f.slug; }}
              onBlurCapture={() => {
                if (fokusert.current === f.slug) fokusert.current = null;
              }}
            >
              {visTittel ? (
                <header className="beskjed__hode">
                  <h3 className="beskjed__til">{f.tittel}</h3>
                </header>
              ) : null}

              <Rikfelt
                dok={t.dok}
                plassholder={plassholder(f)}
                listemodus={listemodus}
                skrivbar={skrivbar === undefined ? true : skrivbar(f)}
                onEndret={(d) => endret(f.slug, d)}
                onFerdig={(d) => ferdig(f.slug, d)}
              />

              <footer className="beskjed__fot">
                <span className={`beskjed__status${t.status === "feil" ? " beskjed__status--feil" : ""}`}>
                  {laster ? "Henter …"
                    : skrivbar !== undefined && !skrivbar(f) ? "Leses her"
                    : statustekst(t)}
                </span>
              </footer>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function statustekst(t: Tilstand): string {
  switch (t.status) {
    case "skriver": return "Skriver …";
    case "lagrer": return "Lagrer …";
    case "feil": return "Fikk ikke lagret. Teksten står her.";
    default: {
      if (t.endret === null || erTom(t.dok)) return "Tom";
      const d = new Date(t.endret);
      if (Number.isNaN(d.getTime())) return "Lagret";
      return `Lagret ${d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}`;
    }
  }
}
